import { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import {
  createTimeSlots,
  getTimeSlots,
  updateTimeSlot,
} from '../../../../api/commonApi'
import {
  isActuallyRunningClocking,
  isClockingItem,
} from '../../../components/common/sourceIcons'
import { toLocalEndOfDay, toLocalStartOfDay } from '../../../matteroomTab/Utils'
import { useTimeEntriesStore } from '../../../stores/useTimeEntriesStore'
import { useUserStore } from '../../../stores/useUserStore'

type UseMidnightClockingOptions = {
  enabled: boolean
  loadTimeEntries: (
    minMinutesValue: any,
    minDailyWorkingHoursValue: any
  ) => Promise<void>
}

const getLoginUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')
  } catch {
    return null
  }
}

export const useMidnightClocking = ({
  enabled,
  loadTimeEntries,
}: UseMidnightClockingOptions) => {
  const selectedUser = useUserStore(state => state.selectedUser)
  const { minMinutes, minDailyWorkingHours } = useTimeEntriesStore()
  const [midnightPrompt, setMidnightPrompt] = useState<any>(null)
  const [midnightDialogVisible, setMidnightDialogVisible] = useState(false)
  const [midnightCountdown, setMidnightCountdown] = useState(15)
  const midnightContinueRef = useRef(false)
  const promptedMidnightKeysRef = useRef(new Set<string>())
  const midnightArmedKeyRef = useRef<string | null>(null)
  const midnightWatcherTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const midnightWatcherIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null)
  const lastApiCheckRef = useRef(0)

  const getMidnightWindowInfo = useCallback(() => {
    const now = moment()
    const midnightAt = now.clone().add(1, 'day').startOf('day')
    const secondsToMidnight = Math.ceil(
      midnightAt.diff(now, 'milliseconds') / 1000
    )

    return {
      now: now.format(),
      dayKey: now.format('YYYY-MM-DD'),
      midnightAt,
      secondsToMidnight,
      isApproaching: secondsToMidnight > 0 && secondsToMidnight <= 15,
    }
  }, [])

  const findSelfRunningClockingSlot = useCallback(async () => {
    const loginUser = getLoginUser()
    if (!loginUser?.id) return null

    const today = moment().format('YYYY-MM-DD')
    const params = [
      `start_date=${toLocalStartOfDay(today)}`,
      `end_date=${toLocalEndOfDay(today)}`,
    ]
    const delegatorId = sessionStorage.getItem('delegators')

    if (delegatorId && +loginUser.id !== +delegatorId) {
      params.push(`delegators=${encodeURIComponent(delegatorId)}`)
    }

    const slots = await getTimeSlots(`?${params.join('&')}`)
    return (
      (slots || []).find(
        (slot: any) => isClockingItem(slot) && isActuallyRunningClocking(slot)
      ) || null
    )
  }, [])

  const clearMidnightWatcherTimers = useCallback(() => {
    if (midnightWatcherTimeoutRef.current) {
      clearTimeout(midnightWatcherTimeoutRef.current)
      midnightWatcherTimeoutRef.current = null
    }

    if (midnightWatcherIntervalRef.current) {
      clearInterval(midnightWatcherIntervalRef.current)
      midnightWatcherIntervalRef.current = null
    }
  }, [])

  const showMidnightPromptForSlot = useCallback(
    (runningSlot: any, reason = 'unknown') => {
      const info = getMidnightWindowInfo()
      if (!runningSlot || !info.isApproaching) return false
      if (promptedMidnightKeysRef.current.has(info.dayKey)) {
        console.log(
          '[MidnightClocking] prompt already handled for',
          info.dayKey
        )
        return false
      }

      promptedMidnightKeysRef.current.add(info.dayKey)
      midnightContinueRef.current = false
      setMidnightCountdown(Math.max(0, info.secondsToMidnight))
      setMidnightPrompt({
        slot: runningSlot,
        stopAt: info.midnightAt.toISOString(),
      })
      setMidnightDialogVisible(true)
      console.log('[MidnightClocking] prompt shown', {
        reason,
        slot: runningSlot,
      })
      return true
    },
    [getMidnightWindowInfo]
  )

  const armMidnightWatcher = useCallback(
    (reason = 'unknown') => {
      if (!enabled) return

      const info = getMidnightWindowInfo()
      const stateKey = info.dayKey

      if (info.isApproaching) return
      if (promptedMidnightKeysRef.current.has(stateKey)) return
      if (midnightArmedKeyRef.current === stateKey) return

      clearMidnightWatcherTimers()
      midnightArmedKeyRef.current = stateKey
      const delayMs = Math.max(0, (info.secondsToMidnight - 20) * 1000)

      // console.log('[MidnightClocking] armMidnightWatcher', {
      //   reason,
      //   stateKey,
      //   secondsToMidnight: info.secondsToMidnight,
      //   delayMs,
      // })

      midnightWatcherTimeoutRef.current = setTimeout(() => {
        midnightWatcherTimeoutRef.current = null
        midnightWatcherIntervalRef.current = setInterval(async () => {
          const cur = getMidnightWindowInfo()
          const curStateKey = cur.dayKey

          if (
            promptedMidnightKeysRef.current.has(curStateKey) ||
            cur.dayKey !== info.dayKey
          ) {
            clearMidnightWatcherTimers()
            midnightArmedKeyRef.current = null
            return
          }

          if (!cur.isApproaching) return

          try {
            const runningSlot = await findSelfRunningClockingSlot()
            console.log('[MidnightClocking] watcher tick', {
              reason,
              dayKey: cur.dayKey,
              secondsToMidnight: cur.secondsToMidnight,
              runningSlot,
            })

            if (runningSlot) {
              const shown = showMidnightPromptForSlot(
                runningSlot,
                `${reason}:watcher`
              )
              if (shown) {
                clearMidnightWatcherTimers()
                midnightArmedKeyRef.current = null
              }
            }
          } catch (err) {
            console.error('[MidnightClocking] watcher check failed:', err)
          }
        }, 3000)
      }, delayMs)
    },
    [
      clearMidnightWatcherTimers,
      enabled,
      findSelfRunningClockingSlot,
      getMidnightWindowInfo,
      showMidnightPromptForSlot,
    ]
  )

  const checkMidnightFromCurrentClocking = useCallback(
    async (reason = 'unknown') => {
      const info = getMidnightWindowInfo()
      // console.log(
      //   '[MidnightClocking] socket check without cached running slot',
      //   {
      //     reason,
      //     now: info.now,
      //     secondsToMidnight: info.secondsToMidnight,
      //     isApproaching: info.isApproaching,
      //   }
      // )

      if (!enabled) return false

      if (!info.isApproaching) {
        armMidnightWatcher(reason)
        return false
      }

      const nowMs = Date.now()
      if (nowMs - lastApiCheckRef.current < 5000) {
        console.log('[MidnightClocking] socket check throttled', { reason })
        return false
      }
      lastApiCheckRef.current = nowMs

      try {
        const runningSlot = await findSelfRunningClockingSlot()
        console.debug('[MidnightClocking] check current clocking', {
          reason,
          secondsToMidnight: info.secondsToMidnight,
          hasRunningSlot: !!runningSlot,
        })
        return showMidnightPromptForSlot(runningSlot, `${reason}:api`)
      } catch (err) {
        console.error(
          `[MidnightClocking] Failed to check running clocking (${reason}):`,
          err
        )
        return false
      }
    },
    [
      armMidnightWatcher,
      enabled,
      findSelfRunningClockingSlot,
      getMidnightWindowInfo,
      showMidnightPromptForSlot,
    ]
  )

  const buildNextDayClockingPayload = useCallback(
    (slot: any, start: string) => {
      const loginUser = getLoginUser()

      return {
        timeentry: slot.timeentry,
        start,
        stop: null,
        non_svc_source: 'Clocking',
        apps: ['Clocking'],
        matter_number: slot.matter_number ?? null,
        matter_name: slot.matter_name ?? null,
        matter_id: slot.matter_id ?? null,
        matter_type: slot.matter_type ?? null,
        client_id: slot.client_id ?? null,
        client_number: slot.client_number ?? null,
        client_name: slot.client_name ?? null,
        is_billable: !!slot.is_billable,
        tkpr_username: slot.tkpr_username ?? loginUser?.email ?? null,
      }
    },
    []
  )

  const handleCancelMidnightClocking = useCallback(() => {
    midnightContinueRef.current = false
    setMidnightDialogVisible(false)
  }, [])

  const handleConfirmMidnightClocking = useCallback(() => {
    midnightContinueRef.current = true
    setMidnightDialogVisible(false)
  }, [])

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    const tick = async () => {
      const info = getMidnightWindowInfo()

      if (
        cancelled ||
        !info.isApproaching ||
        promptedMidnightKeysRef.current.has(info.dayKey)
      ) {
        return
      }

      await checkMidnightFromCurrentClocking(`interval:${info.dayKey}`)
    }

    const intervalId = setInterval(tick, 1000)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkMidnightFromCurrentClocking('visibility')
      }
    }

    checkMidnightFromCurrentClocking('initial')
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearMidnightWatcherTimers()
      midnightArmedKeyRef.current = null
    }
  }, [
    checkMidnightFromCurrentClocking,
    clearMidnightWatcherTimers,
    enabled,
    getMidnightWindowInfo,
  ])

  useEffect(() => {
    if (!enabled || !selectedUser?.id) return
    checkMidnightFromCurrentClocking('delegate-switch')
  }, [checkMidnightFromCurrentClocking, enabled, selectedUser?.id])

  useEffect(() => {
    if (!midnightPrompt?.slot?.id || !midnightPrompt?.stopAt) return

    const updateCountdown = () => {
      const secondsLeft = Math.max(
        0,
        Math.ceil(
          moment(midnightPrompt.stopAt).diff(moment(), 'milliseconds') / 1000
        )
      )
      setMidnightCountdown(secondsLeft)
    }

    const completeAtMidnight = async () => {
      const { slot, stopAt } = midnightPrompt

      try {
        await updateTimeSlot([{ id: slot.id, stop: stopAt }])

        if (midnightContinueRef.current && slot.timeentry != null) {
          await createTimeSlots([buildNextDayClockingPayload(slot, stopAt)])
        }

        await loadTimeEntries(minMinutes, minDailyWorkingHours)
      } catch (err) {
        console.error('[MidnightClocking] Failed to rollover clocking:', err)
      } finally {
        midnightContinueRef.current = false
        setMidnightDialogVisible(false)
        setMidnightPrompt(null)
      }
    }

    updateCountdown()
    const intervalId = setInterval(updateCountdown, 1000)
    const timeoutId = setTimeout(
      completeAtMidnight,
      Math.max(0, moment(midnightPrompt.stopAt).diff(moment()))
    )

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [
    buildNextDayClockingPayload,
    loadTimeEntries,
    midnightPrompt,
    minDailyWorkingHours,
    minMinutes,
  ])

  return {
    midnightDialogVisible,
    midnightCountdown,
    handleCancelMidnightClocking,
    handleConfirmMidnightClocking,
  }
}
